import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class PrintService {

  public async generatePDF(element: HTMLElement, customCssUrl?: string): Promise<void> {
    try {
      console.log('Generazione PDF iniziata...');

      // Trova il contenuto da stampare
      const contentElement = element.querySelector('#modal-print-content') || element;

      // Prepara per la stampa CSS nativa
      await this.preparePrintDocument(contentElement as HTMLElement, customCssUrl);

    } catch (error) {
      console.error('Errore durante la generazione del PDF:', error);
      throw error;
    }
  }

  private async preparePrintDocument(element: HTMLElement, customCssUrl?: string): Promise<void> {
    // Crea un documento temporaneo per la stampa
    const printWindow = window.open('', '_blank', `width=${screen.width},height=${screen.height},left=0,top=0,scrollbars=yes,resizable=yes`);

    if (!printWindow) {
      throw new Error('Impossibile aprire la finestra di stampa. Controlla il blocco popup.');
    }

    // Clona il contenuto
    const clonedContent = element.cloneNode(true) as HTMLElement;

    // Sostituisce icone FontAwesome con SVG inline e app-print-icon con contenuto renderizzato
    this.processIconsForPrint(clonedContent);

    // Prepara il documento HTML per la stampa
    const printDocument = this.createPrintDocument(clonedContent, customCssUrl);

    // Scrivi il documento nella nuova finestra
    printWindow.document.write(printDocument);
    printWindow.document.close();

    // La popup si auto-stampa tramite lo script inline in createPrintDocument.
    // Il parent non controlla il timing: è la popup stessa a sapere quando il suo CSS è pronto.
  }

  private createPrintDocument(content: HTMLElement, customCssUrl?: string): string {
    // Ottieni tutti i CSS esistenti
    const existingStyles = this.extractExistingStyles();

    // Costruisci l'href corretto per il CSS personalizzato
    const isAbsoluteUrl = customCssUrl
      ? (customCssUrl.startsWith('http://') || customCssUrl.startsWith('https://'))
      : false;
    const cssHref = customCssUrl
      ? (isAbsoluteUrl ? customCssUrl : `${window.location.origin}/${customCssUrl}`)
      : '';

    // Link statico nell'<head> (il browser gestisce il caricamento prima del window.load).
    // window.onload nel <body> garantisce che TUTTI i CSS (incluso quello esterno)
    // siano completamente caricati e applicati prima di aprire il dialogo di stampa.
    const customCssLink = cssHref
      ? `\n    <!-- CSS Personalizzato Cliente -->\n    <link rel="stylesheet" href="${cssHref}">`
      : '';

    const printTriggerScript = `<script>
        function triggerPrint() {
          window.focus();
          window.print();
          setTimeout(function() { window.close(); }, 1000);
        }

        window.addEventListener('load', function() {
          var imgs = document.querySelectorAll('img');
          var total = imgs.length;
          var loaded = 0;

          function imgDone() {
            loaded++;
            if (loaded >= total) {
              triggerPrint();
            }
          }

          if (total === 0) {
            triggerPrint();
            return;
          }

          imgs.forEach(function(img) {
            if (img.complete || img.naturalWidth > 0) {
              imgDone();
            } else {
              img.addEventListener('load', imgDone);
              img.addEventListener('error', imgDone);
            }
          });

          // Safety timeout: stampa comunque dopo 5 secondi
          setTimeout(function() {
            if (loaded < total) {
              triggerPrint();
            }
          }, 5000);
        });
      </script>`;

    return `
<!DOCTYPE html>
<html lang="it">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Stampa Viaggio</title>

    <!-- Font Google -->
    <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@300;400;600;700&display=swap" rel="stylesheet">

    <!-- FontAwesome -->
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.1/css/all.min.css">

    <!-- Bootstrap base -->
    <link href="https://cdn.jsdelivr.net/npm/bootstrap@4.5.2/dist/css/bootstrap.min.css" rel="stylesheet">

    <!-- Stili esistenti -->
    ${existingStyles}

    <!-- CSS Print ottimizzato -->
    ${this.getPrintOptimizedCSS()}${customCssLink}
</head>
<body class="print-body">
    <div class="print-container">
        ${content.outerHTML}
    </div>
    ${printTriggerScript}
</body>
</html>`;
  }

  private extractExistingStyles(): string {
    let styles = '';

    // Estrai gli stili dai fogli di stile del documento
    Array.from(document.styleSheets).forEach(sheet => {
      try {
        // Cast per accedere a cssRules con TypeScript 3.8.3
        const cssStyleSheet = sheet as CSSStyleSheet;
        if (cssStyleSheet.cssRules) {
          Array.from(cssStyleSheet.cssRules).forEach(rule => {
            styles += rule.cssText + '\n';
          });
        }
      } catch (e) {
        // Ignora errori CORS per fogli di stile esterni
        console.warn('Impossibile accedere ai CSS rules di un foglio di stile:', e);
      }
    });

    // Rimuovi i selettori di encapsulazione Angular per abbassare la specificità
    // e permettere al CSS esterno del tenant di sovrascrivere gli stili del componente.
    // (Sicuro: questi attributi vengono usati solo nel popup, non nell'app principale.)
    styles = styles
      .replace(/\[_ngcontent-[^\]]*\]/g, '')
      .replace(/\[_nghost-[^\]]*\]/g, '');

    return `<style>${styles}</style>`;
  }


  private getPrintOptimizedCSS(): string {
    return `
<style>
/* ===== RESET E BASE PRINT ===== */
* {
  -webkit-print-color-adjust: exact !important;
  color-adjust: exact !important;
  print-color-adjust: exact !important;
}

@page {
  size: A4;
  margin: 15mm;

  @top-center {
    content: "";
  }

  @bottom-center {
    content: "";
  }
}

html, body {
  margin: 0 !important;
  padding: 0 !important;
  width: 100% !important;
  height: auto !important;
  font-family: 'Playfair Display', 'Georgia', serif !important;
  font-size: 12pt !important;
  line-height: 1.4 !important;
  color: #2c3e50 !important;
  background: white !important;
}

.print-body {
  background: white !important;
}

.print-container {
  width: 100% !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* ===== COVER PAGE PRINT - MANTIENI COME STA ===== */
.travel-cover-page {
  width: 100% !important;
  height: auto !important;
  min-height: 297mm !important;
  page-break-after: always !important;
  page-break-inside: avoid !important;
  position: relative !important;
  display: flex !important;
  flex-direction: column !important;
  background-size: cover !important;
  background-position: center !important;
  overflow: hidden !important;
}

.cover-background-image {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  z-index: 0 !important;
}

.cover-gradient-overlay {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  // background: linear-gradient(135deg, rgba(44, 62, 80, 0.85) 0%, rgba(52, 73, 94, 0.75) 50%, rgba(41, 128, 185, 0.65) 100%) !important;
  z-index: 1 !important;
}

.cover-content-wrapper {
  position: relative !important;
  z-index: 2 !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: space-between !important;
  padding: 3rem 2rem !important;
  color: white !important;
}

/* ===== DREAM TRAVEL CONTENT - RISPETTA CSS ORIGINALE ===== */
.dream-travel-content {
  background: #ffffff !important;
  font-family: 'Playfair Display', 'Georgia', serif !important;
  color: #2c3e50 !important;
  line-height: 1.6 !important;
}

/* ===== JOURNEY CONTENT - SENZA PADDING ORIZZONTALI ===== */
.travel-journey-container {
  padding: 0 !important; /* RIMOSSO PADDING ORIZZONTALE */
}

/* ===== DAY JOURNEY CARDS - RISPETTA STILI ORIGINALI ===== */
.day-journey-card {
  margin-bottom: 2rem !important;
  border-radius: 20px !important;
  margin: 0 !important; /* RIMOSSO MARGIN ORIZZONTALE (era: 0 30px) */
  background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 50%, #f1f3f4 100%) !important;
  position: relative !important;
  page-break-inside: avoid !important;
  break-inside: avoid !important;
}

.day-journey-card.page-break-before {
  page-break-before: always !important;
  break-before: page !important;
}

/* ===== DAY NUMBER BADGE - STILI ORIGINALI ===== */
.day-number-badge {
  position: absolute !important;
  top: 10px !important;
  left: 2rem !important;
  background: linear-gradient(135deg, #3498db, #2980b9) !important;
  color: white !important;
  width: 80px !important;
  height: 80px !important;
  border-radius: 50% !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 3 !important;
}

.day-number {
  font-size: 2rem !important;
  font-weight: bold !important;
  line-height: 1 !important;
  color: white !important;
}

.day-label {
  font-size: 0.7rem !important;
  font-weight: 300 !important;
  letter-spacing: 1px !important;
  color: white !important;
}

/* ===== DAY CONTENT WRAPPER - STILI ORIGINALI ===== */
.day-content-wrapper {
  padding: 3rem 2rem 2rem !important;
}

/* ===== DAY HEADER SECTION - STILI ORIGINALI ===== */
.day-header-section {
  margin-bottom: 2rem !important;
  padding-left: 6rem !important;
}

.day-title {
  font-size: 2.2rem !important;
  color: #2c3e50 !important;
  margin-bottom: 0.5rem !important;
  font-weight: 600 !important;
}

.day-location {
  display: flex !important;
  align-items: center !important;
  color: #7f8c8d !important;
  font-size: 1.2rem !important;
}

.day-location i {
  margin-right: 0.5rem !important;
  color: #e74c3c !important;
}

/* ===== SERVICE SECTIONS - STILI ORIGINALI ===== */
.service-section {
  margin-bottom: 1.5rem !important;
}

.service-section:last-child {
  margin-bottom: 0 !important;
}

.service-header {
  display: flex !important;
  align-items: center !important;
  margin-bottom: 1.5rem !important;
}

.service-title {
  font-size: 1.6rem !important;
  color: #2c3e50 !important;
  margin: 0 !important;
  margin-left: 1rem !important;
  font-weight: 600 !important;
}

/* ===== SERVICE ICON WRAPPER - STILI ORIGINALI ===== */
.service-icon-wrapper {
  width: 50px !important;
  height: 50px !important;
  border-radius: 15px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  color: white !important;
  font-size: 1.3rem !important;
}

.service-icon-wrapper.city-icon {
  background: linear-gradient(135deg, #9b59b6, #8e44ad) !important;
}

.service-icon-wrapper.flight-icon {
  background: linear-gradient(135deg, #3498db, #2980b9) !important;
}

.service-icon-wrapper.hotel-icon {
  background: linear-gradient(135deg, #e67e22, #d35400) !important;
}

.service-icon-wrapper.vehicle-icon {
  background: linear-gradient(135deg, #27ae60, #229954) !important;
}

.service-icon-wrapper.transfer-icon {
  background: linear-gradient(135deg, #f39c12, #e67e22) !important;
}
.service-icon-wrapper.optional-icon {
  background: linear-gradient(135deg, #9b59b6, #8e44ad) !important;
}
.service-icon-wrapper.escorted-icon {
  background: linear-gradient(135deg, #34495e, #2c3e50) !important;
}
.service-icon-wrapper.map-icon {
  background: linear-gradient(135deg, #27ae60, #229954) !important;
}

/* ===== CITY SHOWCASE - STILI ORIGINALI ===== */
.city-showcase {
  display: flex !important;
  gap: 2rem !important;
  align-items: flex-start !important;
}

.city-image-container {
  flex: 1 !important;
  position: relative !important;
  border-radius: 15px !important;
  overflow: hidden !important;
  background: linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%) !important;
  border: 1px solid #f2f2f2 !important;
}

.city-image {
  width: 100% !important;
  height: 200px !important;
  object-fit: cover !important;
}

.image-overlay {
  position: absolute !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  background: rgba(0, 0, 0, 0.4) !important;
  padding: 2rem 1rem 1rem !important;
}

.city-name-overlay {
  color: white !important;
  font-size: 1.5rem !important;
  font-weight: bold !important;
}

.city-description {
  flex: 1 !important;
}

.city-description p {
  font-size: 1.1rem !important;
  color: #555 !important;
  line-height: 1.7 !important;
  font-style: italic !important;
}

/* ===== FLIGHT DETAILS CARD - STILI ORIGINALI ===== */
.flight-details-card {
  border-radius: 15px !important;
  padding: 2rem !important;
  margin-bottom: 1rem !important;
  page-break-inside: avoid !important;
  break-inside: avoid !important;
}

.flight-route {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
}

.airport-info {
  text-align: center !important;
  flex: 1 !important;
}

.airport-code {
  font-size: 1.4rem !important;
  line-height: 1.1 !important;
  font-weight: bold !important;
  // color: #2c3e50 !important;
  margin-bottom: 0.5rem !important;
}

.airport-name {
  font-size: 0.9rem !important;
  // color: #7f8c8d !important;
  margin-bottom: 0.5rem !important;
}

.flight-time {
  font-size: 1.2rem !important;
  // color: #3498db !important;
  font-weight: 600 !important;
}

.flight-path {
  flex: 1 !important;
  position: relative !important;
  margin: 0 2rem !important;
}

.flight-line {
  height: 2px !important;
  background: linear-gradient(90deg, #3498db, #2980b9) !important;
  position: relative !important;
}

.flight-plane-icon {
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  background: white !important;
  color: #3498db !important;
  padding: 4px 9px !important;
  border-radius: 50% !important;
  font-size: 1rem !important;
  width: 34px !important;
  height: 34px !important;
  margin-top: -5px !important;
}

.flight-company {
  text-align: center !important;
  // color: #7f8c8d !important;
  font-style: italic !important;
}

/* ===== MAP SECTION - STILI ORIGINALI ===== */
.print-map-section {
  margin-bottom: 2rem !important;
  page-break-inside: avoid !important;
  break-inside: avoid !important;
}

.print-map-container {
  width: 100% !important;
  border-radius: 15px !important;
  overflow: hidden !important;
  border: 1px solid #f2f2f2 !important;
}

.print-map-image {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  display: block !important;
}

.print-map-container {
  position: relative !important;
}

.print-map-overlay {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  pointer-events: none !important;
}

.print-map-line {
  opacity: 0.8 !important;
}

/* ===== HOTEL SHOWCASE - STILI ORIGINALI ===== */
.hotel-showcase {
  display: flex !important;
  gap: 2rem !important;
  margin-bottom: 2rem !important;
  background: linear-gradient(145deg, #ffffff 0%, #fafafa 50%, #f5f5f5 100%) !important;
  border: 1px solid #f2f2f2 !important;
  border-radius: 15px !important;
  overflow: hidden !important;
  page-break-inside: avoid !important;
  break-inside: avoid !important;
}

.hotel-image-container {
  flex: 1 !important;
  position: relative !important;
}

.hotel-image {
  width: 100% !important;
  height: 240px !important;
  object-fit: cover !important;
}

.hotel-stars {
  position: absolute !important;
  top: 1rem !important;
  left: 1rem !important;
  background: #4a4a4a !important;
  color: #f39c12 !important;
  padding: 0.5rem !important;
  border-radius: 5px !important;
}

.hotel-stars i {
  margin-right: 0.2rem !important;
}

.hotel-details {
  flex: 2 !important;
  padding: 2rem !important;
}

.hotel-name {
  font-size: 1.8rem !important;
  color: #2c3e50 !important;
  margin-bottom: 1rem !important;
  font-weight: 600 !important;
}

.hotel-address {
  display: flex !important;
  align-items: center !important;
  color: #7f8c8d !important;
  margin-bottom: 1rem !important;
}

.hotel-address i {
  margin-right: 0.5rem !important;
  color: #e74c3c !important;
}

.hotel-amenities .amenity-item {
  display: flex !important;
  align-items: center !important;
  margin-bottom: 0.5rem !important;
}

.hotel-amenities .amenity-item i {
  color: #d9650f !important;
  margin-right: 0.5rem !important;
  font-size: 0.9rem !important;
}

.hotel-amenities .amenity-item span {
  color: #555 !important;
  font-size: 0.9rem !important;
}

/* ===== VEHICLE SECTION - STILI ORIGINALI ===== */
.vehicle-card {
  background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%) !important;
  border: 1px solid #f2f2f2 !important;
  border-radius: 15px !important;
  padding: 2rem !important;
  margin-bottom: 1rem !important;
  page-break-inside: avoid !important;
  break-inside: avoid !important;
}

.vehicle-name {
  font-size: 1.5rem !important;
  color: #2c3e50 !important;
  margin-bottom: 1rem !important;
  font-weight: 600 !important;
}

.vehicle-details {
  display: flex !important;
  gap: 2rem !important;
}

.vehicle-detail {
  display: flex !important;
  align-items: center !important;
  color: #555 !important;
}

.vehicle-detail i {
  margin-right: 0.5rem !important;
  color: #27ae60 !important;
}

/* ===== OPTIONAL SERVICES SECTION - STILI ORIGINALI ===== */
.experience-grid {
  display: grid !important;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)) !important;
  gap: 1.5rem !important;
}

.experience-card {
  background: linear-gradient(145deg, #ffffff 0%, #fff8e1 50%, #fff3c4 100%) !important;
  border: 1px solid #fef5e7 !important;
  border-radius: 15px !important;
  padding: 2rem !important;
  display: flex !important;
  align-items: flex-start !important;
  gap: 1rem !important;
  page-break-inside: avoid !important;
  break-inside: avoid !important;
}

.experience-icon {
  width: 50px !important;
  height: 50px !important;
  background: linear-gradient(135deg, #f39c12, #e67e22) !important;
  border-radius: 15px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  color: white !important;
  font-size: 1.2rem !important;
  flex-shrink: 0 !important;
}

.experience-content {
  flex: 1 !important;
}

.experience-name {
  font-size: 1.3rem !important;
  color: #2c3e50 !important;
  margin-bottom: 0.5rem !important;
  font-weight: 600 !important;
}

.experience-description {
  color: #555 !important;
  line-height: 1.6 !important;
  font-style: italic !important;
}

/* ===== FOOTER PAGE STYLES - STILI ORIGINALI ===== */
.travel-footer-page {
  height: 100vh !important;
  background: linear-gradient(135deg, #2c3e50, #34495e, #4a6741) !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  align-items: center !important;
  text-align: center !important;
  color: white !important;
  padding: 4rem !important;
}

.footer-content {
  max-width: 600px !important;
}

.footer-title {
  font-size: 3rem !important;
  margin-bottom: 2rem !important;
  font-weight: 700 !important;
}

.footer-text {
  font-size: 1.3rem !important;
  line-height: 1.8 !important;
  margin-bottom: 3rem !important;
  font-style: italic !important;
}

/* ===== AGGIUNTE PER COMPLETEZZA ===== */
.travel-footer-page .contact-item {
  margin-bottom: 1rem !important;
  font-size: 1.1rem !important;
}

.travel-footer-page .contact-item i {
  margin-right: 0.5rem !important;
  color: #f39c12 !important;
}

/* ===== STAMPA MIGLIORATA ===== */
@media print {
  .no-print {
    display: none !important;
  }

  .page-break-before {
    page-break-before: always !important;
  }

  .page-break-after {
    page-break-after: always !important;
  }

  .page-break-avoid {
    page-break-inside: avoid !important;
  }
}

/* ===== PIANO VOLI SECTION ===== */
.piano-voli-section {
  page-break-after: always !important;
  break-after: page !important;
  margin-bottom: 2rem !important;
}

/* ===== TAPPA JOURNEY CARDS ===== */
.tappa-journey-card {
  position: relative !important;
}

.tappa-journey-card.page-break-before {
  page-break-before: always !important;
  break-before: page !important;
}

/* ===== TAPPA CONTENT WRAPPER ===== */
.tappa-content-wrapper {
  padding: 3rem 2rem 2rem !important;
}

/* ===== ESCORTED / OPTIONAL SERVICE LAYOUT ===== */
.escorted-flex {
  display: flex !important;
  flex-direction: column !important;
  margin-bottom: 1.5rem !important;
}

.escorted-header {
  margin-bottom: 0.5rem !important;
}

.escorted-body-row {
  display: flex !important;
  flex-direction: row !important;
  align-items: flex-start !important;
  gap: 1.5rem !important;
}

.escorted-step-image {
  flex: 0 0 200px !important;
  max-width: 200px !important;
}

.escorted-step-image img.step-photo {
  width: 100% !important;
  height: 150px !important;
  border-radius: 10px !important;
  object-fit: cover !important;
}

.escorted-details {
  flex: 1 1 0% !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 0.5rem !important;
}

/* ===== SERVICE DESCRIPTION — always visible ===== */
.service-description {
  display: block !important;
  visibility: visible !important;
  color: #555 !important;
  font-size: 14px !important;
  line-height: 1.5 !important;
  margin-bottom: 10px !important;
}

/* Force every HTML element that can appear inside a description to be visible.
   This counters any global p { display:none } or similar rules that are
   extracted from the Angular component stylesheet (where they were harmless
   because view-encapsulation scoped them) but become global in the print window. */
.service-description p,
.service-description ul,
.service-description ol {
  display: block !important;
  visibility: visible !important;
  color: inherit !important;
  margin: 0 0 0.4em !important;
}

.service-description li {
  display: list-item !important;
  visibility: visible !important;
  color: inherit !important;
}

.service-description strong,
.service-description b {
  display: inline !important;
  visibility: visible !important;
  font-weight: bold !important;
}

.service-description em,
.service-description i,
.service-description span,
.service-description a {
  display: inline !important;
  visibility: visible !important;
}

.service-type {
  display: block !important;
  color: #7f8c8d !important;
  font-size: 14px !important;
  font-style: italic !important;
}

.service-items {
  display: block !important;
  margin-top: 8px !important;
}

.item-detail {
  display: block !important;
  margin-bottom: 4px !important;
}

.item-name {
  display: block !important;
  color: #5a6c7d !important;
  font-size: 14px !important;
}

/* ===== TRAVEL LOCATIONS SECTION ===== */
.travel-locations-section {
  padding: 2rem !important;
}

.travel-location-item {
  margin-bottom: 2rem !important;
  page-break-inside: avoid !important;
  break-inside: avoid !important;
}

.travel-location-title {
  font-size: 1.6rem !important;
  color: #2c3e50 !important;
  font-weight: 600 !important;
  margin-bottom: 1rem !important;
}

/* ===== NOTES SECTION ===== */
.travel-notes-section {
  padding: 2rem !important;
  break-before: auto !important;
}

.notes-category {
  margin-bottom: 1.5rem !important;
}

.note-item {
  background: #fff !important;
  border-left: 4px solid #3498db !important;
  border-radius: 8px !important;
  padding: 1.2rem 1.5rem !important;
  margin-bottom: 1rem !important;
}

.note-header {
  display: flex !important;
  align-items: center !important;
  margin-bottom: 0.75rem !important;
  page-break-after: avoid !important;
  break-after: avoid !important;
}

.note-title {
  display: block !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  color: #2c3e50 !important;
  margin: 0 !important;
}

/* Force note content and all its HTML children to be visible */
.note-content {
  display: block !important;
  visibility: visible !important;
  color: #555 !important;
  line-height: 1.7 !important;
  font-size: 1rem !important;
}

.note-content p,
.note-content div {
  display: block !important;
  visibility: visible !important;
  color: inherit !important;
  margin-bottom: 0.5rem !important;
}

.note-content ul,
.note-content ol {
  display: block !important;
  visibility: visible !important;
  padding-left: 2rem !important;
  margin-bottom: 0.5rem !important;
}

.note-content li {
  display: list-item !important;
  visibility: visible !important;
  color: inherit !important;
}

.note-content strong,
.note-content b {
  display: inline !important;
  visibility: visible !important;
  font-weight: 600 !important;
}

.note-content em,
.note-content i,
.note-content span,
.note-content a {
  display: inline !important;
  visibility: visible !important;
}
</style>`;
  }

  /** Mappa icone FontAwesome Free 5.15.1 → SVG inline (viewBox + path) */
  private readonly faIcons: Record<string, { vb: string; d: string }> = {
    'hotel':         { vb: '0 0 576 512', d: 'M560 64c8.84 0 16-7.16 16-16V16c0-8.84-7.16-16-16-16H16C7.16 0 0 7.16 0 16v32c0 8.84 7.16 16 16 16h15.98v384H16c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h240v-80c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v80h240c8.84 0 16-7.16 16-16v-32c0-8.84-7.16-16-16-16h-16V64h16zm-304 44.8c0-6.4 6.4-12.8 12.8-12.8h38.4c6.4 0 12.8 6.4 12.8 12.8v38.4c0 6.4-6.4 12.8-12.8 12.8h-38.4c-6.4 0-12.8-6.4-12.8-12.8v-38.4zm0 96c0-6.4 6.4-12.8 12.8-12.8h38.4c6.4 0 12.8 6.4 12.8 12.8v38.4c0 6.4-6.4 12.8-12.8 12.8h-38.4c-6.4 0-12.8-6.4-12.8-12.8v-38.4zm-128-96c0-6.4 6.4-12.8 12.8-12.8h38.4c6.4 0 12.8 6.4 12.8 12.8v38.4c0 6.4-6.4 12.8-12.8 12.8h-38.4c-6.4 0-12.8-6.4-12.8-12.8v-38.4zM179.2 256h-38.4c-6.4 0-12.8-6.4-12.8-12.8v-38.4c0-6.4 6.4-12.8 12.8-12.8h38.4c6.4 0 12.8 6.4 12.8 12.8v38.4c0 6.4-6.4 12.8-12.8 12.8zM192 384c0-53.02 42.98-96 96-96s96 42.98 96 96H192zm256-140.8c0 6.4-6.4 12.8-12.8 12.8h-38.4c-6.4 0-12.8-6.4-12.8-12.8v-38.4c0-6.4 6.4-12.8 12.8-12.8h38.4c6.4 0 12.8 6.4 12.8 12.8v38.4zm0-96c0 6.4-6.4 12.8-12.8 12.8h-38.4c-6.4 0-12.8-6.4-12.8-12.8v-38.4c0-6.4 6.4-12.8 12.8-12.8h38.4c6.4 0 12.8 6.4 12.8 12.8v38.4z' },
    'car':           { vb: '0 0 512 512', d: 'M499.99 176h-59.87l-16.64-41.6C406.38 91.63 365.57 64 319.5 64h-127c-46.06 0-86.88 27.63-103.99 70.4L71.87 176H12.01C4.2 176-1.53 183.34.37 190.91l6 24C7.7 220.25 12.5 224 18.01 224h20.07C24.65 235.73 16 252.78 16 272v48c0 16.12 6.16 30.67 16 41.93V416c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32v-32h256v32c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32v-54.07c9.84-11.25 16-25.8 16-41.93v-48c0-19.22-8.65-36.27-22.07-48H494c5.51 0 10.31-3.75 11.64-9.09l6-24c1.89-7.57-3.84-14.91-11.65-14.91zm-352.06-17.83c7.29-18.22 24.94-30.17 44.57-30.17h127c19.63 0 37.28 11.95 44.57 30.17L384 208H128l19.93-49.83zM96 319.8c-19.2 0-32-12.76-32-31.9S76.8 256 96 256s48 28.71 48 47.85-28.8 15.95-48 15.95zm320 0c-19.2 0-48 3.19-48-15.95S396.8 256 416 256s32 12.76 32 31.9-12.8 31.9-32 31.9z' },
    'plane':         { vb: '0 0 576 512', d: 'M480 192H365.71L260.61 8.06A16.014 16.014 0 0 0 246.71 0h-65.5c-10.63 0-18.3 10.17-15.38 20.39L214.86 192H112l-43.2-57.6c-3.02-4.03-7.77-6.4-12.8-6.4H16.01C5.6 128-2.04 137.78.49 147.88L32 256 .49 364.12C-2.04 374.22 5.6 384 16.01 384H56c5.04 0 9.78-2.37 12.8-6.4L112 320h102.86l-49.03 171.6c-2.92 10.22 4.75 20.4 15.38 20.4h65.5c5.74 0 11.04-3.08 13.89-8.06L365.71 320H480c35.35 0 96-28.65 96-64s-60.65-64-96-64z' },
    'user-friends':  { vb: '0 0 640 512', d: 'M192 256c61.9 0 112-50.1 112-112S253.9 32 192 32 80 82.1 80 144s50.1 112 112 112zm76.8 32h-8.3c-20.8 10-43.9 16-68.5 16s-47.6-6-68.5-16h-8.3C51.6 288 0 339.6 0 403.2V432c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48v-28.8c0-63.6-51.6-115.2-115.2-115.2zM480 256c53 0 96-43 96-96s-43-96-96-96-96 43-96 96 43 96 96 96zm48 32h-3.8c-13.9 4.8-28.6 8-44.2 8s-30.3-3.2-44.2-8H432c-20.4 0-39.2 5.9-55.7 15.4 24.4 26.3 39.7 61.2 39.7 99.8v38.4c0 2.2-.5 4.3-.6 6.4H592c26.5 0 48-21.5 48-48 0-61.9-50.1-112-112-112z' },
    'user-tie':      { vb: '0 0 448 512', d: 'M224 256c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm95.8 32.6L272 480l-32-136 32-56h-96l32 56-32 136-47.8-191.4C56.9 292 0 350.3 0 422.4V464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48v-41.6c0-72.1-56.9-130.4-128.2-133.8z' },
    'shuttle-van':   { vb: '0 0 640 512', d: 'M628.88 210.65L494.39 49.27A48.01 48.01 0 0 0 457.52 32H32C14.33 32 0 46.33 0 64v288c0 17.67 14.33 32 32 32h32c0 53.02 42.98 96 96 96s96-42.98 96-96h128c0 53.02 42.98 96 96 96s96-42.98 96-96h32c17.67 0 32-14.33 32-32V241.38c0-11.23-3.94-22.1-11.12-30.73zM64 192V96h96v96H64zm96 240c-26.51 0-48-21.49-48-48s21.49-48 48-48 48 21.49 48 48-21.49 48-48 48zm160-240h-96V96h96v96zm160 240c-26.51 0-48-21.49-48-48s21.49-48 48-48 48 21.49 48 48-21.49 48-48 48zm-96-240V96h66.02l80 96H384z' },
    'star':          { vb: '0 0 576 512', d: 'M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z' },
    'calculator':    { vb: '0 0 448 512', d: 'M400 0H48C22.4 0 0 22.4 0 48v416c0 25.6 22.4 48 48 48h352c25.6 0 48-22.4 48-48V48c0-25.6-22.4-48-48-48zM128 435.2c0 6.4-6.4 12.8-12.8 12.8H76.8c-6.4 0-12.8-6.4-12.8-12.8v-38.4c0-6.4 6.4-12.8 12.8-12.8h38.4c6.4 0 12.8 6.4 12.8 12.8v38.4zm0-128c0 6.4-6.4 12.8-12.8 12.8H76.8c-6.4 0-12.8-6.4-12.8-12.8v-38.4c0-6.4 6.4-12.8 12.8-12.8h38.4c6.4 0 12.8 6.4 12.8 12.8v38.4zm128 128c0 6.4-6.4 12.8-12.8 12.8h-38.4c-6.4 0-12.8-6.4-12.8-12.8v-38.4c0-6.4 6.4-12.8 12.8-12.8h38.4c6.4 0 12.8 6.4 12.8 12.8v38.4zm0-128c0 6.4-6.4 12.8-12.8 12.8h-38.4c-6.4 0-12.8-6.4-12.8-12.8v-38.4c0-6.4 6.4-12.8 12.8-12.8h38.4c6.4 0 12.8 6.4 12.8 12.8v38.4zm128 128c0 6.4-6.4 12.8-12.8 12.8h-38.4c-6.4 0-12.8-6.4-12.8-12.8V268.8c0-6.4 6.4-12.8 12.8-12.8h38.4c6.4 0 12.8 6.4 12.8 12.8v166.4zm0-256c0 6.4-6.4 12.8-12.8 12.8H76.8c-6.4 0-12.8-6.4-12.8-12.8V76.8C64 70.4 70.4 64 76.8 64h294.4c6.4 0 12.8 6.4 12.8 12.8v102.4z' },
    'sticky-note':   { vb: '0 0 448 512', d: 'M312 320h136V56c0-13.3-10.7-24-24-24H24C10.7 32 0 42.7 0 56v400c0 13.3 10.7 24 24 24h264V344c0-13.2 10.8-24 24-24zm129 55l-98 98c-4.5 4.5-10.6 7-17 7h-6V352h128v6.1c0 6.3-2.5 12.4-7 16.9z' },
    'check-circle':  { vb: '0 0 512 512', d: 'M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z' },
    'info-circle':   { vb: '0 0 512 512', d: 'M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 110c23.196 0 42 18.804 42 42s-18.804 42-42 42-42-18.804-42-42 18.804-42 42-42zm56 254c0 6.627-5.373 12-12 12h-88c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h12v-64h-12c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h64c6.627 0 12 5.373 12 12v100h12c6.627 0 12 5.373 12 12v24z' },
    'map-marker-alt': { vb: '0 0 384 512', d: 'M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z' },
    'map':           { vb: '0 0 576 512', d: 'M0 488c0 13.3 10.7 24 24 24h24c13.3 0 24-10.7 24-24V288h64v200c0 13.3 10.7 24 24 24h24c13.3 0 24-10.7 24-24V144l128 64 128-64v344c0 13.3 10.7 24 24 24h24c13.3 0 24-10.7 24-24V56c0-10.4-6.7-19.6-16.5-22.9L384.5 1.3c-3.4-1.1-7-1.3-10.5-.5L256 24 138 1c-3.5-.8-7.1-.6-10.5.5L16.5 33.1C6.7 36.4 0 45.6 0 56v432z' },
    'bed':           { vb: '0 0 640 512', d: 'M176 256c44.11 0 80-35.89 80-80s-35.89-80-80-80-80 35.89-80 80 35.89 80 80 80zm352-128H304c-8.84 0-16 7.16-16 16v144H64V80c0-8.84-7.16-16-16-16H16C7.16 64 0 71.16 0 80v352c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16v-48h512v48c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16V240c0-61.86-50.14-112-112-112z' },
    'building':      { vb: '0 0 448 512', d: 'M436 480h-20V24c0-13.255-10.745-24-24-24H56C42.745 0 32 10.745 32 24v456H12c-6.627 0-12 5.373-12 12v20h448v-20c0-6.627-5.373-12-12-12zM128 76c0-6.627 5.373-12 12-12h40c6.627 0 12 5.373 12 12v40c0 6.627-5.373 12-12 12h-40c-6.627 0-12-5.373-12-12V76zm0 96c0-6.627 5.373-12 12-12h40c6.627 0 12 5.373 12 12v40c0 6.627-5.373 12-12 12h-40c-6.627 0-12-5.373-12-12v-40zm52 148h-40c-6.627 0-12-5.373-12-12v-40c0-6.627 5.373-12 12-12h40c6.627 0 12 5.373 12 12v40c0 6.627-5.373 12-12 12zm76 160h-64v-84c0-6.627 5.373-12 12-12h40c6.627 0 12 5.373 12 12v84zm64-172c0 6.627-5.373 12-12 12h-40c-6.627 0-12-5.373-12-12v-40c0-6.627 5.373-12 12-12h40c6.627 0 12 5.373 12 12v40zm0-96c0 6.627-5.373 12-12 12h-40c-6.627 0-12-5.373-12-12v-40c0-6.627 5.373-12 12-12h40c6.627 0 12 5.373 12 12v40zm0-96c0 6.627-5.373 12-12 12h-40c-6.627 0-12-5.373-12-12V76c0-6.627 5.373-12 12-12h40c6.627 0 12 5.373 12 12v40z' },
    'chair':         { vb: '0 0 448 512', d: 'M112 128c0-29.5 16.2-55 40-68.9V256h48V48h48v208h48V59.1c23.8 13.9 40 39.4 40 68.9v128h48V128C384 57.3 326.7 0 256 0h-64C121.3 0 64 57.3 64 128v128h48zm334.3 213.9l-10.7-32c-4.4-13.1-16.6-21.9-30.4-21.9H42.7c-13.8 0-26 8.8-30.4 21.9l-10.7 32C-5.2 362.6 10.2 384 32 384v112c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V384h256v112c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V384c21.8 0 37.2-21.4 30.3-42.1z' },
    'cog':           { vb: '0 0 512 512', d: 'M487.4 315.7l-42.6-24.6c4.3-23.2 4.3-47 0-70.2l42.6-24.6c4.9-2.8 7.1-8.6 5.5-14-11.1-35.6-30-67.8-54.7-94.6-3.8-4.1-10-5.1-14.8-2.3L380.8 110c-17.9-15.4-38.5-27.3-60.8-35.1V25.8c0-5.6-3.9-10.5-9.4-11.7-36.7-8.2-74.3-7.8-109.2 0-5.5 1.2-9.4 6.1-9.4 11.7V75c-22.2 7.9-42.8 19.8-60.8 35.1L88.7 85.5c-4.9-2.8-11-1.9-14.8 2.3-24.7 26.7-43.6 58.9-54.7 94.6-1.7 5.4.6 11.2 5.5 14L67.3 221c-4.3 23.2-4.3 47 0 70.2l-42.6 24.6c-4.9 2.8-7.1 8.6-5.5 14 11.1 35.6 30 67.8 54.7 94.6 3.8 4.1 10 5.1 14.8 2.3l42.6-24.6c17.9 15.4 38.5 27.3 60.8 35.1v49.2c0 5.6 3.9 10.5 9.4 11.7 36.7 8.2 74.3 7.8 109.2 0 5.5-1.2 9.4-6.1 9.4-11.7v-49.2c22.2-7.9 42.8-19.8 60.8-35.1l42.6 24.6c4.9 2.8 11 1.9 14.8-2.3 24.7-26.7 43.6-58.9 54.7-94.6 1.5-5.5-.7-11.3-5.6-14.1zM256 336c-44.1 0-80-35.9-80-80s35.9-80 80-80 80 35.9 80 80-35.9 80-80 80z' },
    'calendar-alt':  { vb: '0 0 448 512', d: 'M0 464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V192H0v272zm320-196c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12h-40c-6.6 0-12-5.4-12-12v-40zm0 128c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12h-40c-6.6 0-12-5.4-12-12v-40zM192 268c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12h-40c-6.6 0-12-5.4-12-12v-40zm0 128c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12h-40c-6.6 0-12-5.4-12-12v-40zM64 268c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12H76c-6.6 0-12-5.4-12-12v-40zm0 128c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12H76c-6.6 0-12-5.4-12-12v-40zM400 64h-48V16c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v48H160V16c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v48H48C21.5 64 0 85.5 0 112v48h448v-48c0-26.5-21.5-48-48-48z' },
    'calendar':      { vb: '0 0 448 512', d: 'M12 192h424c6.6 0 12 5.4 12 12v260c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V204c0-6.6 5.4-12 12-12zm436-44v-36c0-26.5-21.5-48-48-48h-48V12c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v52H160V12c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v52H48C21.5 64 0 85.5 0 112v36c0 6.6 5.4 12 12 12h424c6.6 0 12-5.4 12-12z' },
    'receipt':       { vb: '0 0 384 512', d: 'M358.4 3.2L320 48 265.6 3.2a15.9 15.9 0 0 0-19.2 0L192 48 137.6 3.2a15.9 15.9 0 0 0-19.2 0L64 48 25.6 3.2C15-4.7 0 2.8 0 16v480c0 13.2 15 20.7 25.6 12.8L64 464l54.4 44.8a15.9 15.9 0 0 0 19.2 0L192 464l54.4 44.8a15.9 15.9 0 0 0 19.2 0L320 464l38.4 44.8c10.5 7.9 25.6.4 25.6-12.8V16c0-13.2-15-20.7-25.6-12.8zM320 360c0 4.4-3.6 8-8 8H72c-4.4 0-8-3.6-8-8v-16c0-4.4 3.6-8 8-8h240c4.4 0 8 3.6 8 8v16zm0-96c0 4.4-3.6 8-8 8H72c-4.4 0-8-3.6-8-8v-16c0-4.4 3.6-8 8-8h240c4.4 0 8 3.6 8 8v16zm0-96c0 4.4-3.6 8-8 8H72c-4.4 0-8-3.6-8-8v-16c0-4.4 3.6-8 8-8h240c4.4 0 8 3.6 8 8v16z' },
    'plane-departure': { vb: '0 0 640 512', d: 'M624 448H16c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h608c8.84 0 16-7.16 16-16v-32c0-8.84-7.16-16-16-16zM80.55 341.27c6.28 6.84 15.1 10.72 24.33 10.71l130.54-.18a65.62 65.62 0 0 0 29.64-7.12l290.96-147.65c26.74-13.57 50.71-32.94 67.02-58.31 18.31-28.48 20.3-49.09 13.07-63.65-7.21-14.57-24.74-25.27-58.25-27.45-29.85-1.94-59.54 5.92-86.28 19.48l-98.51 49.99-218.7-82.06a17.799 17.799 0 0 0-18-1.11L90.62 67.29c-10.67 5.41-13.25 19.65-5.17 28.53l156.22 98.1-103.21 52.38-72.35-36.47a17.804 17.804 0 0 0-16.07.02L9.91 230.22c-10.44 5.3-13.19 19.12-5.57 28.08l76.21 82.97z' },
    'car-side':      { vb: '0 0 640 512', d: 'M544 192h-16L419.22 56.02A64.025 64.025 0 0 0 369.24 32H155.33c-26.17 0-49.7 15.93-59.42 40.23L48 194.26C20.44 201.4 0 226.21 0 256v112c0 8.84 7.16 16 16 16h48c0 53.02 42.98 96 96 96s96-42.98 96-96h128c0 53.02 42.98 96 96 96s96-42.98 96-96h48c8.84 0 16-7.16 16-16v-80c0-53.02-42.98-96-96-96zM160 432c-26.47 0-48-21.53-48-48s21.53-48 48-48 48 21.53 48 48-21.53 48-48 48zm72-240H116.93l38.4-96H232v96zm48 0V96h89.24l76.8 96H280zm200 240c-26.47 0-48-21.53-48-48s21.53-48 48-48 48 21.53 48 48-21.53 48-48 48z' },
    'store':         { vb: '0 0 616 512', d: 'M602 118.6L537.1 15C531.3 5.7 521 0 510 0H106C95 0 84.7 5.7 78.9 15L14 118.6c-33.5 53.5-3.8 127.9 58.8 136.4 4.5.6 9.1.9 13.7.9 29.6 0 55.8-13 73.8-33.1 18 20.1 44.3 33.1 73.8 33.1 29.6 0 55.8-13 73.8-33.1 18 20.1 44.3 33.1 73.8 33.1 29.6 0 55.8-13 73.8-33.1 18.1 20.1 44.3 33.1 73.8 33.1 4.7 0 9.2-.3 13.7-.9 62.8-8.4 92.6-82.8 59-136.4zM529.5 288c-10 0-19.9-1.5-29.5-3.8V384H116v-99.8c-9.6 2.2-19.5 3.8-29.5 3.8-6 0-12.1-.4-18-1.2-5.6-.8-11.1-2.1-16.4-3.6V480c0 17.7 14.3 32 32 32h448c17.7 0 32-14.3 32-32V283.2c-5.4 1.6-10.8 2.9-16.4 3.6-6.1.8-12.1 1.2-18.2 1.2z' },
    'door-open':     { vb: '0 0 640 512', d: 'M624 448h-80V113.45C544 86.19 522.47 64 496 64H384v64h96v384h144c8.84 0 16-7.16 16-16v-32c0-8.84-7.16-16-16-16zM312.24 1.01l-192 49.74C105.99 54.44 96 67.7 96 82.92V448H16c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h336V33.18c0-21.58-19.56-37.41-39.76-32.17zM264 288c-13.25 0-24-14.33-24-32s10.75-32 24-32 24 14.33 24 32-10.75 32-24 32z' },
    'user':          { vb: '0 0 448 512', d: 'M224 256c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm89.6 32h-16.7c-22.2 10.2-46.9 16-72.9 16s-50.6-5.8-72.9-16h-16.7C60.2 288 0 348.2 0 422.4V464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48v-41.6c0-74.2-60.2-134.4-134.4-134.4z' },
    'suitcase':      { vb: '0 0 512 512', d: 'M128 480h256V80c0-26.5-21.5-48-48-48H176c-26.5 0-48 21.5-48 48v400zm64-384h128v32H192V96zm320 80v256c0 26.5-21.5 48-48 48h-48V128h48c26.5 0 48 21.5 48 48zM96 480H48c-26.5 0-48-21.5-48-48V176c0-26.5 21.5-48 48-48h48v352z' },
    'snowflake':     { vb: '0 0 448 512', d: 'M440.3 345.2l-33.8-19.5 26-7c8.2-2.2 13.1-10.7 10.9-18.9l-4-14.9c-2.2-8.2-10.7-13.1-18.9-10.9l-70.8 19-63.9-37 63.8-36.9 70.8 19c8.2 2.2 16.7-2.7 18.9-10.9l4-14.9c2.2-8.2-2.7-16.7-10.9-18.9l-26-7 33.8-19.5c7.4-4.3 9.9-13.7 5.7-21.1L430.4 119c-4.3-7.4-13.7-9.9-21.1-5.7l-33.8 19.5 7-26c2.2-8.2-2.7-16.7-10.9-18.9l-14.9-4c-8.2-2.2-16.7 2.7-18.9 10.9l-19 70.8-62.8 36.2v-77.5l53.7-53.7c6.2-6.2 6.2-16.4 0-22.6l-11.3-11.3c-6.2-6.2-16.4-6.2-22.6 0L256 56.4V16c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v40.4l-19.7-19.7c-6.2-6.2-16.4-6.2-22.6 0L138.3 48c-6.3 6.2-6.3 16.4 0 22.6l53.7 53.7v77.5l-62.8-36.2-19-70.8c-2.2-8.2-10.7-13.1-18.9-10.9l-14.9 4c-8.2 2.2-13.1 10.7-10.9 18.9l7 26-33.8-19.5c-7.4-4.3-16.8-1.7-21.1 5.7L2.1 145.7c-4.3 7.4-1.7 16.8 5.7 21.1l33.8 19.5-26 7c-8.3 2.2-13.2 10.7-11 19l4 14.9c2.2 8.2 10.7 13.1 18.9 10.9l70.8-19 63.8 36.9-63.8 36.9-70.8-19c-8.2-2.2-16.7 2.7-18.9 10.9l-4 14.9c-2.2 8.2 2.7 16.7 10.9 18.9l26 7-33.8 19.6c-7.4 4.3-9.9 13.7-5.7 21.1l15.5 26.8c4.3 7.4 13.7 9.9 21.1 5.7l33.8-19.5-7 26c-2.2 8.2 2.7 16.7 10.9 18.9l14.9 4c8.2 2.2 16.7-2.7 18.9-10.9l19-70.8 62.8-36.2v77.5l-53.7 53.7c-6.3 6.2-6.3 16.4 0 22.6l11.3 11.3c6.2 6.2 16.4 6.2 22.6 0l19.7-19.7V496c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16v-40.4l19.7 19.7c6.2 6.2 16.4 6.2 22.6 0l11.3-11.3c6.2-6.2 6.2-16.4 0-22.6L256 387.7v-77.5l62.8 36.2 19 70.8c2.2 8.2 10.7 13.1 18.9 10.9l14.9-4c8.2-2.2 13.1-10.7 10.9-18.9l-7-26 33.8 19.5c7.4 4.3 16.8 1.7 21.1-5.7l15.5-26.8c4.3-7.3 1.8-16.8-5.6-21z' },
    'users':         { vb: '0 0 640 512', d: 'M96 224c35.3 0 64-28.7 64-64s-28.7-64-64-64-64 28.7-64 64 28.7 64 64 64zm448 0c35.3 0 64-28.7 64-64s-28.7-64-64-64-64 28.7-64 64 28.7 64 64 64zm32 32h-64c-17.6 0-33.5 7.1-45.1 18.6 40.3 22.1 68.9 62 75.1 109.4h66c17.7 0 32-14.3 32-32v-32c0-35.3-28.7-64-64-64zm-256 0c61.9 0 112-50.1 112-112S381.9 32 320 32 208 82.1 208 144s50.1 112 112 112zm76.8 32h-8.3c-20.8 10-43.9 16-68.5 16s-47.6-6-68.5-16h-8.3C179.6 288 128 339.6 128 403.2V432c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48v-28.8c0-63.6-51.6-115.2-115.2-115.2zm-223.7-13.4C161.5 263.1 145.6 256 128 256H64c-35.3 0-64 28.7-64 64v32c0 17.7 14.3 32 32 32h65.9c6.3-47.4 34.9-87.3 75.2-109.4z' },
    'globe':         { vb: '0 0 496 512', d: 'M336.5 160C322 70.7 287.8 8 248 8s-74 62.7-88.5 152h177zM152 256c0 22.2 1.2 43.5 3.3 64h185.3c2.1-20.5 3.3-41.8 3.3-64s-1.2-43.5-3.3-64H155.3c-2.1 20.5-3.3 41.8-3.3 64zm324.7-96c-28.6-67.9-86.5-120.4-158-141.6 24.4 33.8 41.2 84.7 50 141.6h108zM177.2 18.4C105.8 39.6 47.8 92.1 19.3 160h108c8.7-56.9 25.5-107.8 49.9-141.6zM487.4 192H372.7c2.1 21 3.3 42.5 3.3 64s-1.2 43-3.3 64h114.6c5.5-20.5 8.6-41.8 8.6-64s-3.1-43.5-8.5-64zM120 256c0-21.5 1.2-43 3.3-64H8.6C3.2 212.5 0 233.8 0 256s3.2 43.5 8.6 64h114.6c-2-21-3.2-42.5-3.2-64zm39.5 96c14.5 89.3 48.7 152 88.5 152s74-62.7 88.5-152h-177zm159.3 141.6c71.4-21.2 129.4-73.7 158-141.6h-108c-8.8 56.9-25.6 107.8-50 141.6zM19.3 352c28.6 67.9 86.5 120.4 158 141.6-24.4-33.8-41.2-84.7-50-141.6h-108z' },
    'percentage':    { vb: '0 0 384 512', d: 'M109.25 173.25c24.99-24.99 24.99-65.52 0-90.51-24.99-24.99-65.52-24.99-90.51 0-24.99 24.99-24.99 65.52 0 90.51 25 25 65.52 25 90.51 0zm256 165.49c-24.99-24.99-65.52-24.99-90.51 0-24.99 24.99-24.99 65.52 0 90.51 24.99 24.99 65.52 24.99 90.51 0 25-24.99 25-65.51 0-90.51zm-1.94-231.43l-22.62-22.62c-12.5-12.5-32.76-12.5-45.25 0L20.69 359.44c-12.5 12.5-12.5 32.76 0 45.25l22.62 22.62c12.5 12.5 32.76 12.5 45.25 0l274.75-274.75c12.5-12.49 12.5-32.75 0-45.25z' },
    'shield-alt':    { vb: '0 0 512 512', d: 'M466.5 83.7l-192-80a48.15 48.15 0 0 0-36.9 0l-192 80C27.7 91.1 16 108.6 16 128c0 198.5 114.5 335.7 221.5 380.3 11.8 4.9 25.1 4.9 36.9 0C360.1 472.6 496 349.3 496 128c0-19.4-11.7-36.9-29.5-44.3zM256.1 446.3l-.1-381 175.9 73.3c-3.3 151.4-82.1 261.1-175.8 307.7z' },
    'envelope':      { vb: '0 0 512 512', d: 'M502.3 190.8c3.9-3.1 9.7-.2 9.7 4.7V400c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V195.6c0-5 5.7-7.8 9.7-4.7 22.4 17.4 52.1 39.5 154.1 113.6 21.1 15.4 56.7 47.8 92.2 47.6 35.7.3 72-32.8 92.3-47.6 102-74.1 131.6-96.3 154-113.7zM256 320c23.2.4 56.6-29.2 73.4-41.4 132.7-96.3 142.8-104.7 173.4-128.7 5.8-4.5 9.2-11.5 9.2-18.9v-19c0-26.5-21.5-48-48-48H48C21.5 64 0 85.5 0 112v19c0 7.4 3.4 14.3 9.2 18.9 30.6 23.9 40.7 32.4 173.4 128.7 16.8 12.2 50.2 41.8 73.4 41.4z' },
    'phone':         { vb: '0 0 512 512', d: 'M493.4 24.6l-104-24c-11.3-2.6-22.9 3.3-27.5 13.9l-48 112c-4.2 9.8-1.4 21.3 6.9 28l60.6 49.6c-36 76.7-98.9 140.5-177.2 177.2l-49.6-60.6c-6.8-8.3-18.2-11.1-28-6.9l-112 48C3.9 366.5-2 378.1.6 389.4l24 104C27.1 504.2 36.7 512 48 512c256.1 0 464-207.5 464-464 0-11.2-7.7-20.9-18.6-23.4z' },
    'print':         { vb: '0 0 512 512', d: 'M448 192V77.25c0-8.49-3.37-16.62-9.37-22.63L393.37 9.37c-6-6-14.14-9.37-22.63-9.37H96C78.33 0 64 14.33 64 32v160c-35.35 0-64 28.65-64 64v112c0 8.84 7.16 16 16 16h48v96c0 17.67 14.33 32 32 32h320c17.67 0 32-14.33 32-32v-96h48c8.84 0 16-7.16 16-16V256c0-35.35-28.65-64-64-64zm-64 256H128v-96h256v96zm0-224H128V64h192v48c0 8.84 7.16 16 16 16h48v96zm48 72c-13.25 0-24-10.75-24-24 0-13.26 10.75-24 24-24s24 10.74 24 24c0 13.25-10.75 24-24 24z' },
  };

  /**
   * Pre-processa il contenuto clonato per la stampa:
   * – Unwrap <app-print-icon> → ne estrae il figlio renderizzato
   * – <i class="fas fa-xxx"> → lo converte in <svg> inline
   * – <img> con src relativo → lo rende assoluto
   */
  private processIconsForPrint(element: HTMLElement): void {
    const origin = window.location.origin;

    // 1) Unwrap <app-print-icon> sostituendolo con il contenuto renderizzato da Angular
    const iconHosts = element.querySelectorAll('app-print-icon');
    iconHosts.forEach(host => {
      const child = host.firstElementChild;
      if (!child) return;
      if (child.tagName === 'IMG') {
        const src = child.getAttribute('src');
        if (src && !src.startsWith('http://') && !src.startsWith('https://')) {
          child.setAttribute('src', origin + '/' + src.replace(/^\//, ''));
        }
      }
      if (child.tagName === 'I') {
        const iconName = this.extractFaIconName(child.className);
        if (iconName) {
          const svg = this.faToSvg(iconName, child.className);
          child.parentNode?.replaceChild(svg, child);
        }
      }
      // Sostituisce l'host Angular con il suo figlio
      host.parentNode?.replaceChild(child, host);
    });

    // 2) Converte tutti gli <i class="fas fa-xxx"> rimasti in SVG inline
    const faElements = element.querySelectorAll('i[class*="fa-"]');
    faElements.forEach(i => {
      const iconName = this.extractFaIconName(i.className);
      if (iconName) {
        const svg = this.faToSvg(iconName, i.className);
        i.parentNode?.replaceChild(svg, i);
      }
    });

    // 3) Rende assolute le URL relative delle immagini e upgrada HTTP→HTTPS su pagine sicure
    const isSecureContext = window.location.protocol === 'https:';
    const imgs = element.querySelectorAll('img[src]');
    imgs.forEach(img => {
      let src = img.getAttribute('src');
      if (!src) return;
      if (!src.startsWith('http://') && !src.startsWith('https://')) {
        src = origin + '/' + src.replace(/^\//, '');
      }
      if (isSecureContext && src.startsWith('http://')) {
        src = 'https://' + src.substring(7);
      }
      img.setAttribute('src', src);
    });
  }

  /** Estrae il nome dell'icona FontAwesome da una stringa di classi (es. "fas fa-hotel" → "hotel") */
  private extractFaIconName(className: string): string | null {
    const match = className.match(/\bfa-([a-z][a-z0-9-]*)\b/);
    return match ? match[1] : null;
  }

  /** Converte un nome icona FontAwesome in un elemento <svg> inline */
  private faToSvg(iconName: string, className: string): SVGElement {
    const icon = this.faIcons[iconName];
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', icon?.vb || '0 0 512 512');
    svg.setAttribute('class', className);
    svg.setAttribute('width', '1em');
    svg.setAttribute('height', '1em');
    svg.setAttribute('fill', 'currentColor');
    svg.setAttribute('style', 'vertical-align: middle; -webkit-print-color-adjust: exact; print-color-adjust: exact;');

    if (icon) {
      const path = document.createElementNS(ns, 'path');
      path.setAttribute('d', icon.d);
      svg.appendChild(path);
    }
    return svg;
  }

}

